import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Link2 } from "lucide-react";
import { toast } from "sonner";
import { notifyApiError } from "../../lib/toast-error";
import type { CanHolder } from "../../lib/access";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useIncidentOperationsLog, useLinkOperationsLog, useOperationsLog } from "./api";
import { OperationsLogPeriod, OperationsLogTypeBadge } from "./entry-parts";
import { canLinkOperationsLog } from "./permissions";

/**
 * The Betriebslog card on the incident detail page (Hauptansicht 3:
 * "verknuepften Betriebslog-Eintraegen"; [F-013], [US-012], [AC-014]): the
 * linked entries, each opening its own detail, and - for whoever holds
 * `oplog.link` on this incident by its `can` - the link action.
 */
export function IncidentOperationsLogCard({
  incidentId,
  incident,
}: {
  incidentId: string;
  incident: CanHolder;
}): JSX.Element {
  const { t } = useTranslation();
  const linkedQuery = useIncidentOperationsLog(incidentId);
  const [linkOpen, setLinkOpen] = useState(false);
  const linked = linkedQuery.data?.items ?? [];
  const mayLink = canLinkOperationsLog(incident);

  return (
    <Card data-testid="incident-detail_operations-log">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-sm font-medium text-muted-foreground">{t("operationsLog.card.title")}</CardTitle>
        {mayLink ? (
          <Button
            size="sm"
            variant="outline"
            data-testid="incident-detail_operations-log-link"
            onClick={() => setLinkOpen(true)}
          >
            <Link2 className="h-4 w-4" aria-hidden="true" />
            {t("operationsLog.card.link")}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {linkedQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        ) : linked.length === 0 ? (
          <p data-testid="incident-detail_operations-log-empty" className="text-sm text-muted-foreground">
            {t("operationsLog.card.empty")}
          </p>
        ) : (
          <ul className="space-y-3">
            {linked.map((entry) => (
              <li key={entry.id}>
                <Link
                  to="/operations-log/$id"
                  params={{ id: entry.id }}
                  data-testid={`incident-detail_operations-log-entry_${entry.id}`}
                  className="block space-y-1 rounded-md border p-2 hover:bg-accent"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <OperationsLogTypeBadge type={entry.type} />
                    <span className="text-xs text-muted-foreground">
                      <OperationsLogPeriod startAt={entry.startAt} endAt={entry.endAt} />
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{entry.shortDescription}</p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      {mayLink ? (
        <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
          <DialogContent data-testid="incident-detail_operations-log-link-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t("operationsLog.card.linkTitle")}</DialogTitle>
              <DialogDescription>{t("operationsLog.card.linkIntro")}</DialogDescription>
            </DialogHeader>
            {linkOpen ? (
              <LinkCandidates
                incidentId={incidentId}
                linkedIds={linked.map((entry) => entry.id)}
                onLinked={() => setLinkOpen(false)}
              />
            ) : null}
            <DialogFooter>
              <Button
                variant="outline"
                data-testid="incident-detail_operations-log-link-close"
                onClick={() => setLinkOpen(false)}
              >
                {t("operationsLog.card.close")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </Card>
  );
}

/** The entries one may pick, searched by Kurzbeschreibung, newest start first. */
function LinkCandidates({
  incidentId,
  linkedIds,
  onLinked,
}: {
  incidentId: string;
  linkedIds: readonly string[];
  onLinked: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const handle = window.setTimeout(() => setQ(search.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [search]);
  const candidates = useOperationsLog({ ...(q ? { q } : {}), limit: 20 });
  const link = useLinkOperationsLog(incidentId);
  const items = candidates.data?.pages.flatMap((page) => page.items) ?? [];

  const onLink = (entryId: string): void => {
    link.mutate(entryId, {
      onSuccess: () => {
        toast.success(t("operationsLog.card.linked"));
        onLinked();
      },
      onError: (error) =>
        notifyApiError(error, {
          precondition: t("errors.precondition"),
          forbidden: t("errors.forbidden"),
          conflict: t("errors.conflict"),
          generic: t("errors.generic"),
        }),
    });
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <label htmlFor="operations-log-link-search" className="text-sm font-medium">
          {t("operationsLog.card.searchLabel")}
        </label>
        <Input
          id="operations-log-link-search"
          data-testid="incident-detail_operations-log-link-search"
          value={search}
          placeholder={t("operationsLog.card.searchPlaceholder")}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {candidates.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <p data-testid="incident-detail_operations-log-link-empty" className="text-sm text-muted-foreground">
          {t("operationsLog.card.noCandidates")}
        </p>
      ) : (
        <ul data-testid="incident-detail_operations-log-candidates" className="max-h-80 space-y-2 overflow-y-auto">
          {items.map((entry) => {
            const already = linkedIds.includes(entry.id);
            return (
              <li key={entry.id} className="flex items-start justify-between gap-3 rounded-md border p-2">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <OperationsLogTypeBadge type={entry.type} />
                    <span className="text-xs text-muted-foreground">
                      <OperationsLogPeriod startAt={entry.startAt} endAt={entry.endAt} />
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{entry.shortDescription}</p>
                </div>
                {already ? (
                  <span className="shrink-0 text-xs text-muted-foreground">{t("operationsLog.card.alreadyLinked")}</span>
                ) : (
                  <Button
                    size="sm"
                    className="shrink-0"
                    data-testid={`incident-detail_operations-log-link-pick_${entry.id}`}
                    disabled={link.isPending}
                    onClick={() => onLink(entry.id)}
                  >
                    {link.isPending ? t("operationsLog.card.linking") : t("operationsLog.card.linkAction")}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
